import {
  Button,
  ComboBox,
  InputMaskField,
  LoadingIndicator,
  TextInput,
  Toggler,
} from '@components';
import { saldoMATDocs, saldoMATInitialValues, saldoMATSchema } from '@schemas';
import { cpfResponsavelValido, parentesco as p } from '@utils';
import * as yup from 'yup';
import { Field, Form, Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { enviarRequerimentoFormulario } from '../../../../store/requerimentos/thunks';
import { getLimite } from '../../../../store/loan/thunks';

import { DadosUsuario } from './components/DadosUsuario';
import { ModalUpload } from './components/ModalUpload';
import {
  PesquisaTendencia,
  opcoesMotivoRetiradaPesquisaTendencia,
  pesquisaTendenciaInitialValues,
} from './components/PesquisaTendencia';
import { validaUpload } from './utils/validaUpload';

const Anexos = ({ parentesco = '' }) => {
  const anexosArray = saldoMATDocs[parentesco];
  const [modalIsOpen, setModalIsOpen] = useState(false);

  return (
    <>
      <ModalUpload modalIsOpen={modalIsOpen} setModalIsOpen={setModalIsOpen} />
      {saldoMATDocs.commons.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                return (
                  <TextInput
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return validaUpload({
                        anexo,
                        e,
                        form,
                        setModalIsOpen,
                      });
                    }}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}

      {(anexosArray || []).map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                return (
                  <TextInput
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return validaUpload({
                        anexo,
                        e,
                        form,
                        setModalIsOpen,
                      });
                    }}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}

      {saldoMATDocs.others.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                return (
                  <TextInput
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return validaUpload({
                        anexo,
                        e,
                        form,
                        setModalIsOpen,
                      });
                    }}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}
    </>
  );
};

export const SaldoMAT = ({ title, reference }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const envioLoading = useSelector(
    (state) => state.requerimentos.envio.loading
  );

  const { requestDone, saldoDevedor } = useSelector(
    (state) => state.loan.limite
  );

  const [showForm, setShowForm] = useState(false);

  const pesquisaTendenciaCondicionalSchema = yup.object({
    motivo_temp: yup.string().when(['optoPor', 'parcelaAVista'], {
      is: (optoPor, parcelaAVista) =>
        optoPor === 'Receber valor à vista' || parcelaAVista === 'Sim',
      then: yup
        .string()
        .oneOf(opcoesMotivoRetiradaPesquisaTendencia.map((o) => o.id))
        .required('Atenção, selecione o motivo.'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    instituicao_temp: yup
      .string()
      .when(['motivo_temp', 'optoPor', 'parcelaAVista'], {
        is: (motivoTemp, optoPor, parcelaAVista) => {
          const precisaQuestionario =
            optoPor === 'Receber valor à vista' || parcelaAVista === 'Sim';

          return (
            precisaQuestionario &&
            motivoTemp === 'Investir em outra instituição'
          );
        },
        then: (field) =>
          field.trim().required('Atenção, informe a instituição.'),
        otherwise: (field) => field.notRequired().nullable(),
      }),

    outro_temp: yup.string().when(['motivo_temp', 'optoPor', 'parcelaAVista'], {
      is: (motivoTemp, optoPor, parcelaAVista) => {
        const precisaQuestionario =
          optoPor === 'Receber valor à vista' || parcelaAVista === 'Sim';

        return precisaQuestionario && motivoTemp === 'Outros';
      },
      then: (field) => field.trim().required('Atenção, especifique.'),
      otherwise: (field) => field.notRequired().nullable(),
    }),

    pesquisaTendencia: yup.string().when(['optoPor', 'parcelaAVista'], {
      is: (optoPor, parcelaAVista) =>
        optoPor === 'Receber valor à vista' || parcelaAVista === 'Sim',
      then: yup.string().required('Atenção, responda o questionário.'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  });

  useEffect(() => {
    if (!requestDone) {
      dispatch(getLimite());
    }
  }, [dispatch, requestDone]);

  return !requestDone ? (
    <LoadingIndicator />
  ) : (
    <div className="card">
      <div className="card-body">
        <h3 className="fw-bold ts-20 mb-32">{title}</h3>

        {showForm ? (
          <>
            <p className="mb-3">
              Venho, por meio deste, requerer à Fundação Forluminas de
              Seguridade Social - FORLUZ, que me seja pago o saldo remanescente
              existente na Conta de Aposentadoria do ex-participante abaixo
              discriminado, nos termos do Regulamento do Plano Misto de
              Benefícios Previdenciários da Forluz (Plano B) e das normas
              vigentes. Para tanto, anexo a documentação necessária para o fim.
            </p>
            <Formik
              validateOnMount
              initialValues={{
                ...saldoMATInitialValues,
                ...pesquisaTendenciaInitialValues,
              }}
              validationSchema={saldoMATSchema(saldoDevedor).concat(
                pesquisaTendenciaCondicionalSchema
              )}
              onSubmit={(values) => {
                dispatch(
                  enviarRequerimentoFormulario({
                    campos: values,
                    formulario: reference,
                  })
                )
                  .unwrap()
                  .then(() => {
                    navigate('/requerimentos/andamento');
                  });
              }}
            >
              {({
                values,
                handleSubmit,
                isValid,
                setFieldValue,
                setFieldTouched,
              }) => (
                <Form>
                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="parentesco"
                        component={ComboBox}
                        options={p.options.parentescosSemFE.map((x) => {
                          return { value: x, label: x };
                        })}
                        label="Parentesco do beneficiário com o titular"
                      />
                    </div>
                  </div>

                  <div className="row form-row-flex mb-3">
                    <div className="form-row-son">
                      <Field
                        className="form-row-g-son"
                        name="cpfResponsavelLegal"
                        mask="111.111.111-11"
                        inputMode="numeric"
                        label="CPF do(a) Tutor(a)/Responsável Legal do beneficiário"
                      >
                        {({ field, form }) => (
                          <InputMaskField
                            {...field}
                            mask="111.111.111-11"
                            inputMode="numeric"
                            label="CPF do(a) Tutor(a)/Responsável Legal do beneficiário"
                            onChange={(e) => {
                              form.setFieldValue('cpfResponsavelLegal', e);
                              if (!cpfResponsavelValido(e)) {
                                form.setFieldValue('nomeDoTutor', '');
                              }
                            }}
                          />
                        )}
                      </Field>
                    </div>

                    <div className="form-row-son">
                      <Field
                        className="form-row-g-son"
                        name="nomeDoTutor"
                        tooltip="Pessoa que possui a responsabilidade pelo beneficiário através de EX: Tutela, Curatela ou Responsável legal."
                        component={TextInput}
                        label="Nome do(a) Tutor(a)/Responsável Legal do beneficiário (somente se possuir)"
                        disabled={
                          !cpfResponsavelValido(values.cpfResponsavelLegal)
                        }
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="optoPor"
                        component={ComboBox}
                        label="Opto por:"
                        options={[
                          'Receber valor à vista',
                          'Continuar a receber o último percentual escolhido pelo participante',
                        ].map((x) => {
                          return { value: x, label: x };
                        })}
                        tooltip="Caso opte por Receber valor à vista: Desta forma, dou à Forluz a mais completa, rasa e irrevogável quitação e nada mais tenho a reclamar, a qualquer título, em juízo ou fora dele."
                        onChange={(op) => {
                          if (op.value === 'Receber valor à vista') {
                            setFieldValue('adiantamentoQuinzenal', '', false);
                            setFieldValue('parcelaAVista', '', false);

                            setFieldTouched(
                              'adiantamentoQuinzenal',
                              false,
                              false
                            );
                            setFieldTouched('parcelaAVista', false, false);
                          } else if (values.parcelaAVista !== 'Sim') {
                            setFieldValue('motivo_temp', '', false);
                            setFieldValue('instituicao_temp', '', false);
                            setFieldValue('outro_temp', '', false);
                            setFieldValue('pesquisaTendencia', '', false);

                            setFieldTouched('motivo_temp', false, false);
                            setFieldTouched('instituicao_temp', false, false);
                            setFieldTouched('outro_temp', false, false);
                            setFieldTouched('pesquisaTendencia', false, false);
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <Field
                        disabled={!(values.optoPor !== 'Receber valor à vista')}
                        className="mb-3"
                        name="parcelaAVista"
                        component={ComboBox}
                        tooltip="Retirada antecipada de até 30% do saldo de conta; acarreta redução proporcional no valor do benefício. Haverá incidência de Imposto de Renda conforme regime de tributação vigente e o saldo devedor do empréstimo, se houver, será descontado a mesma proporção da sua retirada."
                        options={['Sim', 'Não'].map((x) => {
                          return { value: x, label: x };
                        })}
                        label="Parcela à Vista"
                        onChange={(op) => {
                          if (op.value === 'Não') {
                            setFieldValue('retiradaAVista', '', false);

                            setFieldTouched('retiradaAVista', false, false);

                            if (values.optoPor !== 'Receber valor à vista') {
                              setFieldValue('motivo_temp', '', false);
                              setFieldValue('instituicao_temp', '', false);
                              setFieldValue('outro_temp', '', false);
                              setFieldValue('pesquisaTendencia', '', false);

                              setFieldTouched('motivo_temp', false, false);
                              setFieldTouched('instituicao_temp', false, false);
                              setFieldTouched('outro_temp', false, false);
                              setFieldTouched(
                                'pesquisaTendencia',
                                false,
                                false
                              );
                            }
                          }
                        }}
                      />
                    </div>

                    <div className="col-md-12 col-lg-6">
                      <Field
                        disabled={!(values.parcelaAVista === 'Sim')}
                        className="mb-3"
                        name="retiradaAVista"
                        label="Percentual retirada à vista"
                        component={TextInput}
                        type="number"
                        min="0"
                        max="50"
                      />
                    </div>
                  </div>

                  {(values.optoPor === 'Receber valor à vista' ||
                    values.parcelaAVista === 'Sim') && <PesquisaTendencia />}

                  {!!saldoDevedor && values.parcelaAVista === 'Sim' && (
                    <div className="row">
                      <div className="col-md-12 col-lg-6">
                        <Field
                          className="mb-3"
                          name="boletoEmprestimo"
                          component={ComboBox}
                          tooltip="Boleto para quitação integral do saldo devedor remanescente será encaminhado por e-mail do endereço de e-mail cadastrado com vencimento no último dia útil do mês seguinte ao requerimento de benefício."
                          options={['Sim', 'Não'].map((x) => {
                            return { value: x, label: x };
                          })}
                          label="Boleto para quitação de empréstimo"
                          onChange={() => {
                            setFieldValue('abatimentoDoEmprestimo', '', false);
                          }}
                        />
                      </div>

                      {values.boletoEmprestimo === 'Não' && (
                        <div className="col-md-12 col-lg-6">
                          <Field
                            className="mb-3"
                            name="abatimentoDoEmprestimo"
                            component={ComboBox}
                            tooltip="Antecipação:	Modalidade de pagamento em que prazo final do contrato é reduzido ( abatimento das últimas parcelas) e o valor da parcela mantido. Nesta modalidade o montante de juros é reduzido em função do encurtamento do contrato. Amortização:	Modalidade de pagamento em que o valor da parcela é reduzido e o prazo contratado mantido. "
                            options={['Antecipação', 'Amortização'].map((x) => {
                              return { value: x, label: x };
                            })}
                            label="Abatimento do empréstimo"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <Field
                        disabled={!(values.optoPor !== 'Receber valor à vista')}
                        className="mb-3"
                        name="adiantamentoQuinzenal"
                        component={ComboBox}
                        label="Adiantamento quinzenal?"
                        tooltip="Antecipação de 35% do valor bruto, pago no dia 15 de cada mês."
                        options={['Sim', 'Não'].map((x) => {
                          return { value: x, label: x };
                        })}
                      />
                    </div>

                    <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="acessoContraCheque"
                        label="Acesso ao contracheque via"
                        component={ComboBox}
                        options={['Portal', 'E-mail e portal'].map((x) => {
                          return { value: x, label: x };
                        })}
                      />
                    </div>

                    {/* <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="acessoJornalForluz"
                        component={ComboBox}
                        label="Acesso Jornal Forluz"
                        options={['Digital', 'Impresso'].map((x) => {
                          return { value: x, label: x };
                        })}
                      />
                    </div> */}
                  </div>

                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <p className="mb-4">
                        Declaro estar ciente de que a Forluz operacionalizará os
                        descontos decorrentes de eventual relacionamento com as
                        Entidades Externas (ABCF; AEA; AIC; ANAPAR; CECREMEC;
                        CEMIG SAÚDE; CLIC; GREMIG; SEGURO DE VIDA; SINDICATO
                        JUIZ DE FORA; SINDIELETRO; SINDSUL), por meio de
                        descontos no meu benefício para repasse à(s)
                        Entidade(s), os quais poderão ser visualizados
                        mensalmente no contracheque. Declaro estar ciente,
                        ainda, de que devo contatar diretamente a Entidade à
                        qual sou filiado para o cancelamento das cobranças e/ou
                        ressarcimento de qualquer valor.
                      </p>
                      <Field
                        className="mb-3"
                        name="consentimentoDescontoFolha"
                        component={Toggler}
                        label="Consentimento descontos em folha (obrigatório)"
                      />

                      <Field
                        className="mb-3"
                        name="consentimentoDadosSensiveis"
                        component={Toggler}
                        label="Com base na Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais), autorizo o tratamento dos meus dados pessoais e dados pessoais sensíveis para fins relacionados ao meu vínculo com a FORLUZ."
                      />
                    </div>

                    <Field
                      className="mb-3"
                      name="consentimentoAcertoFinanceiro"
                      component={Toggler}
                      createTermsField
                      label="Declaro estar ciente e concordo que, em razão de
                          falecimento do participante, ajustes poderão ser
                          feitos no meu contracheque, incluindo créditos e
                          débitos oriundos de resíduos e adiantamentos
                          anteriores ao óbito, ou valores processados
                          indevidamente após essa data."
                    />

                    <Field
                      className="mb-3"
                      name="aceiteInss"
                      component={Toggler}
                      createTermsField
                      label="Estou ciente que pessoa aposentada pela Previdência Oficial (INSS) e acometida por moléstia grave faz jus à isenção de Imposto de Renda. Este pedido poderá ser solicitado à Forluz a qualquer tempo através do formulário Isenção de Imposto de Renda disponível no menu Requerimentos. Se deferida, a isenção será processada no mês seguinte ao requerimento. "
                    />
                  </div>

                  <Anexos parentesco={values.parentesco} />

                  <Field
                    className="mb-3"
                    name="declaracaoVeracidade"
                    component={Toggler}
                    createTermsField
                    label="Declaro que as informações e os documentos por mim apresentados à Forluz são verdadeiros, estando ciente de que, caso não sejam autênticos, estarei sujeito às consequências civis e criminais cabíveis, além de autorizar a Forluz a anular o ato praticado ou o benefício concedido."
                  />

                  <hr className="mt-32 mt-xs-0 mb-32" />
                  <div className="row">
                    <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                      <div className="d-block d-md-flex align-items-center">
                        <Button
                          disabled={!isValid || envioLoading}
                          className="ms-0 ms-md-3"
                          onClick={handleSubmit}
                        >
                          Continuar
                        </Button>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <DadosUsuario
            onClick={() => {
              window.scroll(0, 0);
              setShowForm(true);
            }}
            dadosBancarios
          />
        )}
      </div>
    </div>
  );
};
